import { type RefObject, useEffect, useState } from "react";

/** Automatic motion only runs while its owning carousel can be seen. */
export function useAutoplayActivity(
	ref: RefObject<HTMLElement | null>,
	enabled: boolean,
	respectReducedMotion = true,
) {
	const [active, setActive] = useState(false);
	useEffect(() => {
		const element = ref.current;
		if (!enabled || !element) return;
		const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
		let inView = typeof IntersectionObserver === "undefined";
		const update = () =>
			setActive(
				inView &&
					!document.hidden &&
					(!respectReducedMotion || !preference.matches),
			);
		const observer =
			typeof IntersectionObserver === "undefined"
				? undefined
				: new IntersectionObserver((entries) => {
						inView = entries.some((entry) => entry.isIntersecting);
						update();
					});
		observer?.observe(element);
		preference.addEventListener("change", update);
		document.addEventListener("visibilitychange", update);
		update();
		return () => {
			observer?.disconnect();
			preference.removeEventListener("change", update);
			document.removeEventListener("visibilitychange", update);
		};
	}, [enabled, ref, respectReducedMotion]);
	return enabled && active;
}
